import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ColumnDef } from '@tanstack/react-table'
import to from 'await-to-js'
import { toast } from 'react-toastify'
import {
  ActionsDropdown,
  createEditAction,
  createDeleteAction,
} from 'components/Buttons/ActionsDropdown'
import dialogConfirm from 'components/dialogConfirm'
import { TextareaInput } from 'components/FormUtils/TextareaInput'
import { DescriptionCell } from 'components/Tables/CommonTableCells/DescriptionCell'
import { SalesStage, SalesStageColor } from 'models/sales_stages'
import { useMemo, useRef, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { useForm, Controller } from 'react-hook-form'
import {
  PiCaretDown,
  PiCheck,
  PiLock,
  PiMagnifyingGlass,
  PiPlus,
  PiWarningCircle,
} from 'react-icons/pi'
import useApiService from 'services/api'
import { ClientDataTable } from '../../../components/ClientDataTable/ClientDataTable'
import { DataTableContainer } from '../../../components/DataTable/UI'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import FbButton from '../../../components/FbUI/FbButton'
import { BootstrapInput } from '../../../components/FormUtils/NumericInput'
import { Modal } from '../../../components/UI/Modal/Modal'
import { handleFormError } from '../../../utils/handleFormError'
import { StagePill } from 'components/FbUI/StagePill'
import {
  ReorderStagesModal,
  type ReorderStagesModalHandle,
} from './ReorderStagesModal'
import { Ds2026ButtonProvider } from 'components/FbUI/Ds2026ButtonContext'
import {
  getStageColorKey,
  isTerminalStage,
  STAGE_COLOR_LABELS,
  STAGE_PALETTE,
  STAGE_PALETTE_KEYS,
} from 'components/SalesStage/stageColors'
import { cn } from 'components/UI/cn'
import { usePreferences } from 'context/preferences/PreferencesContext'

type StageFormValues = {
  stage: string
  win_rate: number
  avg_days_to_close: number
  description: string
  /** Badge color; null means "auto-assign from pipeline position". */
  color: SalesStageColor | null
}

const defaultValuesStage: StageFormValues = {
  stage: '',
  win_rate: 0,
  avg_days_to_close: 0,
  description: '',
  color: null,
}

/**
 * Dropdown color picker for the create/edit stage modal: a trigger showing the
 * current choice, opening a scrollable list of Auto + the gated palette. Each
 * row shows the color, its name, and a warning flag when another stage already
 * uses that color (duplicates are allowed, just warned).
 */
function StageColorField({
  value,
  onChange,
  colorUsers,
}: {
  value: SalesStageColor | null
  onChange: (value: SalesStageColor | null) => void
  colorUsers: (key: SalesStageColor) => string[]
}) {
  const [open, setOpen] = useState(false)
  const pick = (next: SalesStageColor | null) => {
    onChange(next)
    setOpen(false)
  }
  return (
    <div className="mt-3 flex flex-col">
      <label className="mb-1 select-none text-base font-medium text-fg">
        Color
      </label>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between rounded-md border border-line-form bg-surface px-3 py-2 text-left text-base text-fg"
      >
        <span className="flex items-center gap-2">
          {value ? (
            <span
              className={cn('size-4 rounded-full', STAGE_PALETTE[value].dot)}
            />
          ) : (
            <span className="flex size-4 items-center justify-center rounded-full border border-dashed border-line text-xxs font-semibold text-fg-secondary">
              A
            </span>
          )}
          {value ? STAGE_COLOR_LABELS[value] : 'Auto (by pipeline order)'}
        </span>
        <PiCaretDown size={14} className="text-fg-secondary" />
      </button>

      {open && (
        <div className="mt-1 max-h-56 overflow-y-auto rounded-md border border-line bg-surface py-1 shadow-sm">
          <button
            type="button"
            onClick={() => pick(null)}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition hover:bg-neutral-100"
          >
            <span className="flex size-4 flex-shrink-0 items-center justify-center rounded-full border border-dashed border-line text-xxs font-semibold text-fg-secondary">
              A
            </span>
            <span className="flex-1 text-fg">Auto (by pipeline order)</span>
            {value === null && (
              <PiCheck size={14} className="flex-shrink-0 text-fg-secondary" />
            )}
          </button>

          {STAGE_PALETTE_KEYS.map((key) => {
            const usedBy = colorUsers(key)
            const selected = value === key
            return (
              <button
                key={key}
                type="button"
                onClick={() => pick(key)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition hover:bg-neutral-100"
              >
                <span
                  className={cn(
                    'size-4 flex-shrink-0 rounded-full',
                    STAGE_PALETTE[key].dot
                  )}
                />
                <span className="flex-1 text-fg">
                  {STAGE_COLOR_LABELS[key]}
                </span>
                {usedBy.length > 0 && (
                  <span
                    className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-warning-strong"
                    title={`Already used by ${usedBy.join(', ')}`}
                  >
                    <PiWarningCircle size={12} className="flex-shrink-0" />
                    {usedBy[0]}
                    {usedBy.length > 1 ? ` +${usedBy.length - 1}` : ''}
                  </span>
                )}
                {selected && (
                  <PiCheck
                    size={14}
                    className="flex-shrink-0 text-fg-secondary"
                  />
                )}
              </button>
            )
          })}
        </div>
      )}

      <span className="mt-1 text-sm font-normal leading-5 text-fg-secondary">
        Pick a badge color shown across the app, or leave on Auto to assign one
        by pipeline order. Duplicates are allowed — a{' '}
        <PiWarningCircle
          size={13}
          className="inline-block align-[-2px] text-warning-strong"
        />{' '}
        flags a color already in use. Closed Won / Lost / Churn keep their fixed
        colors.
      </span>
    </div>
  )
}

export function SalesStageTable() {
  const api = useApiService()
  const queryClient = useQueryClient()
  const { isSettingEnabled } = usePreferences()
  const customOrderEnabled = isSettingEnabled('sales_stage_custom_order')
  const [showModal, setShowModal] = useState<boolean>(false)
  const [selectedStageID, setSelectedStageID] = useState<number>()
  const [searchQuery, setSearchQuery] = useState('')
  // When the create-stage modal was launched from the Organize Stages modal,
  // we reopen Organize once the stage is saved so the user lands back where
  // they were with the new stage ready to place.
  const reorderModalRef = useRef<ReorderStagesModalHandle>(null)
  const [reopenOrganizeAfterModal, setReopenOrganizeAfterModal] =
    useState(false)

  const {
    data: stageData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['stages'],
    queryFn: async () => {
      return await api.getSalesStages()
    },
  })

  // Always present rows in the persisted pipeline order (the sequence the
  // reorder modal writes), with terminal stages locked to the bottom. Sorting
  // by `order` keeps this table consistent with the modal and the rest of the
  // app instead of drifting with avg_days_to_close.
  const sortedData = useMemo(() => {
    return stageData?.toSorted((a, b) => {
      const aTerminal = isTerminalStage(a)
      const bTerminal = isTerminalStage(b)
      if (aTerminal !== bTerminal) return aTerminal ? 1 : -1
      return a.order - b.order
    })
  }, [stageData])

  // Client-side search: all data is loaded at once (no pagination), so filtering in-memory is sufficient.
  const data = useMemo(() => {
    if (!searchQuery || !sortedData) return sortedData
    const q = searchQuery.toLowerCase()
    return sortedData.filter(
      (item) =>
        item.stage?.toLowerCase().includes(q) ||
        item.description?.toLowerCase().includes(q)
    )
  }, [sortedData, searchQuery])

  /** Names of OTHER stages whose EFFECTIVE color (stored or derived-by-order)
   *  matches a given color — drives the picker's duplicate warning. Excludes
   *  the stage being edited and terminal outcomes. */
  const colorUsers = (key: SalesStageColor): string[] =>
    (stageData ?? [])
      .filter(
        (s) =>
          s.id !== selectedStageID &&
          !isTerminalStage(s) &&
          getStageColorKey(s, stageData ?? [], customOrderEnabled) === key
      )
      .map((s) => s.stage)

  const {
    control: control,
    handleSubmit: handleSubmit,
    formState: { isSubmitting, isValid },
    setError,
    reset,
  } = useForm<StageFormValues>({
    defaultValues: defaultValuesStage,
    mode: 'onSubmit',
  })

  function openModal() {
    setShowModal(true)
  }

  /** Launch the create modal from Organize Stages, flagged to return there. */
  function handleCreateFromOrganize() {
    setReopenOrganizeAfterModal(true)
    openModal()
  }

  function hideModal(freshStages?: SalesStage[]) {
    setShowModal(false)
    reset(defaultValuesStage)
    setSelectedStageID(undefined)
    // If this create/edit flow came from Organize Stages, hand control back to
    // it. freshStages (passed on a successful save) carries the just-created or
    // just-edited stage so Organize re-buckets with the latest data immediately.
    if (reopenOrganizeAfterModal) {
      setReopenOrganizeAfterModal(false)
      reorderModalRef.current?.open(freshStages)
    }
  }

  const onSubmitCreate = handleSubmit(async (values) => {
    const [err] = await to(
      api.postSalesStage({
        avg_days_to_close: values.avg_days_to_close,
        stage: values.stage,
        win_rate: values.win_rate,
        description: values.description,
        color: values.color,
      })
    )
    // Keep the modal open on failure so the user can correct and retry (and so
    // we don't bounce back to Organize on a stage that never got created).
    if (err) {
      handleFormError(err, setError)
      return
    }

    const { data: freshStages } = await refetch()
    hideModal(freshStages)
  })

  const onSubmitUpdate = handleSubmit(async (values) => {
    if (!selectedStageID) return

    const [err] = await to(
      api.putStage(selectedStageID, {
        avg_days_to_close: values.avg_days_to_close,
        stage: values.stage,
        win_rate: values.win_rate,
        description: values.description,
        color: values.color,
      })
    )
    // Keep the modal open on failure so the user can correct and retry (and so
    // we don't bounce back to Organize on a stage that never got updated).
    if (err) {
      handleFormError(err, setError)
      return
    }

    // Pass the freshly-refetched list so an edit launched from Organize reopens
    // it with the updated stage already reflected.
    const { data: freshStages } = await refetch()
    hideModal(freshStages)
  })

  async function handleRowDelete(index: number) {
    const stage = data?.[index]
    if (!stage?.id) return

    // Block deletion if stage is in use by deals or automations
    const warnings: string[] = []
    if (stage.deals_count > 0) {
      warnings.push(
        `${stage.deals_count} deal${stage.deals_count !== 1 ? 's' : ''}`
      )
    }
    if (stage.automations_count > 0) {
      warnings.push(
        `${stage.automations_count} automation${stage.automations_count !== 1 ? 's' : ''}`
      )
    }

    if (warnings.length > 0) {
      await dialogConfirm(
        `Stage "${stage.stage}" cannot be deleted because it is currently referenced by ${warnings.join(' and ')}.`,
        'Cannot Delete Stage',
        'Please reassign these references before deleting this stage.'
      )
      return
    }

    if (
      await dialogConfirm(
        'Are you sure you want to delete this stage?',
        'Delete Stage'
      )
    ) {
      try {
        await api.deleteStage(stage.id)
        void refetch()
        void queryClient.invalidateQueries({ queryKey: ['stages'] })
      } catch {
        // Error toast handled by baseAPICall
      }
    }
  }

  /** Open the edit modal for a stage, pre-filling the form from its values. */
  function openEditForStage(stage: SalesStage) {
    setShowModal(true)
    setSelectedStageID(stage.id)
    // Pre-fill the stored color (null = auto-assigned by pipeline order, which
    // the picker shows as "Auto").
    reset({
      stage: stage.stage,
      win_rate: stage.win_rate,
      avg_days_to_close: stage.avg_days_to_close,
      description: stage.description,
      color: stage.color ?? null,
    })
  }

  function handleRowEdit(index: number) {
    const stage = data?.[index]
    if (!stage) return
    openEditForStage(stage)
  }

  /** Edit a stage from the Organize modal, flagged to return there on save. */
  function handleEditFromOrganize(stageId: number) {
    const stage = stageData?.find((s) => s.id === stageId)
    if (!stage) return
    setReopenOrganizeAfterModal(true)
    openEditForStage(stage)
  }

  async function handleBulkReorder(
    orderedStageIds: number[],
    deletedStageIds: number[] = []
  ) {
    try {
      // Delete staged removals first (concurrently) so the reorder only
      // sequences survivors — avoids transient gaps against the company+order
      // unique constraint.
      if (deletedStageIds.length > 0) {
        await Promise.all(deletedStageIds.map((id) => api.deleteStage(id)))
      }
      await api.bulkReorderSalesStages(orderedStageIds)
    } catch (e) {
      // Surface the failure and rethrow so the modal stays open for a retry.
      toast.error('Failed to save stage changes. Please try again.')
      throw e
    } finally {
      // Always resync so a partial change (e.g. one deletion landed but the
      // reorder didn't) can't leave a stale, inconsistent stage list.
      void refetch()
      void queryClient.invalidateQueries({ queryKey: ['stages'] })
    }
  }

  const columns = useMemo<ColumnDef<SalesStage, any>[]>(
    () => [
      {
        accessorKey: 'stage',
        header: 'Stage Name',
        size: 200,
        meta: { isFirstColumn: true },
        cell: (info) => {
          return <StagePill stageId={info.row.original.id} />
        },
      },
      {
        accessorKey: 'description',
        header: 'Description',
        size: 400,
        cell: (info) => <DescriptionCell value={info.getValue()} />,
      },
      {
        accessorKey: 'win_rate',
        header: 'Win Rate (%)',
        size: 130,
      },
      {
        accessorKey: 'avg_days_to_close',
        header: 'Avg Days to Close',
        size: 150,
      },
      {
        accessorKey: 'deals_count',
        header: 'Deals',
        size: 110,
        cell: ({ row }) => {
          if (row.original?.deals_count) {
            return row.original.deals_count.toLocaleString('en-US')
          }

          return 0
        },
      },
      {
        id: 'actions',
        header: () => <div className="w-full text-center">Actions</div>,
        size: 70,
        meta: {
          isLastColumn: true,
          headerDisableOrdering: true,
        },
        cell: ({ row }) => {
          if (
            row.original.stage === 'Closed Won' ||
            row.original.stage === 'Closed Lost' ||
            row.original.stage === 'Churn'
          )
            return (
              <div
                className="flex w-full justify-center text-gray-500"
                title="Required stage — cannot be edited or deleted"
              >
                <PiLock size={16} />
              </div>
            )
          return (
            <ActionsDropdown
              className="justify-center"
              actions={[
                createEditAction(() => handleRowEdit(row.index)),
                createDeleteAction(() => handleRowDelete(row.index)),
              ]}
            />
          )
        },
      },
    ],
    [data]
  )

  if (isLoading || !sortedData) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '500px',
          width: '100%',
        }}
      >
        <Spinner animation="border" />
      </div>
    )
  }

  return (
    <TableLinkStyleProvider variant="plain">
      <div className="flex flex-col w-full flex-1 min-h-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
        <DataTableContainer className="h-full">
          <ClientDataTable
            tableHeader={
              <div
                data-table-toolbar
                className="flex flex-row items-center justify-between gap-3 px-4 py-3"
              >
                <div className="relative w-full max-w-md">
                  <PiMagnifyingGlass
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
                  />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search stages..."
                    className="w-full h-9 pl-9 pr-3 text-sm bg-white border border-gray-300 rounded-lg text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-brand-active focus:shadow-[0_0_0_4px_rgba(8,116,67,0.2)]"
                  />
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <ReorderStagesModal
                    ref={reorderModalRef}
                    stages={sortedData ?? []}
                    onSave={handleBulkReorder}
                    onCreateStage={handleCreateFromOrganize}
                    onEditStage={handleEditFromOrganize}
                  />
                  <FbButton
                    variant="primary"
                    onClick={() => openModal()}
                    className="!bg-brand-bright !text-white hover:!bg-brand-active"
                  >
                    <PiPlus size={16} />
                    Create Stage
                  </FbButton>
                </div>
              </div>
            }
            height="100%"
            stickyLastColumn
            data={data ?? []}
            columns={columns}
            enableColumnResizing
            rowHeight={44}
            headerHeight={40}
            tableKey="settings-sales-stages-table"
          />
          <Ds2026ButtonProvider>
            <Modal
              open={showModal}
              title={selectedStageID ? 'Edit Sales Stage' : 'Create New Stage'}
              description={
                selectedStageID ? (
                  <>
                    Edit your sales stage to reflect your current process and
                    performance.
                    <br />
                    &quot;Note that changes may change LTV and revenue
                    estimates.
                  </>
                ) : (
                  <>
                    Create a sales stage to reflect your current process and its
                    performance.
                    <br />
                    Note that changes may change LTV and revenue estimates.
                  </>
                )
              }
              onOpenChange={(open) => !open && hideModal()}
              loading={isSubmitting}
              blockAccept={!isValid}
              onAccept={selectedStageID ? onSubmitUpdate : onSubmitCreate}
              acceptButtonText={selectedStageID ? 'Update' : 'Create'}
            >
              <BootstrapInput
                name="stage"
                control={control}
                type="text"
                label="Stage Name"
                placeholder="Sales Stage"
              />
              <BootstrapInput
                name="win_rate"
                control={control}
                type="text"
                label="Win Rate (%)"
                placeholder="Win Rate"
                units={'%'}
              />
              <BootstrapInput
                name="avg_days_to_close"
                control={control}
                type="text"
                label="Average Days to Close"
                placeholder="Average Days to Close"
              />
              <TextareaInput
                name="description"
                control={control}
                type="textarea"
                label="Description"
                placeholder="Description"
              />
              <Controller
                name="color"
                control={control}
                render={({ field }) => (
                  <StageColorField
                    value={field.value}
                    onChange={field.onChange}
                    colorUsers={colorUsers}
                  />
                )}
              />
            </Modal>
          </Ds2026ButtonProvider>
        </DataTableContainer>
      </div>
    </TableLinkStyleProvider>
  )
}
